import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import StatusDropdown from '@/components/common/StatusComponent';
import Layout from '@/components/Layout/Layout';
import AssignDriver from '@/components/order/AssignDriver';
import OrderStatusUpdate from '@/components/order/OrderStatus';
import Card from '@/components/UI/Card';
import DateRangePicker from '@/components/UI/DateRange';
import { useListQueryParams } from '@/hooks/useListQueryParams';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { ActionConfig } from '@/interfaces/common.interface';
import {
  IOrderResponse,
  IOrderStates,
  Order,
} from '@/interfaces/order.interface';
import {
  formatDateTime,
  formatDeliveryRange,
  GetFilterUrl,
  getPaymentMethodMessage,
  orderFilterOption,
  OrderStatus,
  showToastNotification,
  toFixedThree,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useMemo, useState } from 'react';
import { Range } from 'react-date-range';
import { useNavigate } from 'react-router-dom';

const Orders: React.FC = () => {
  const { canView, canEdit } = usePermissions();
  const navigate = useNavigate();

  // ✅ URL-driven filters
  const { params, setParams } = useListQueryParams({
    search: '',
    orderStatus: '',
    paymentStatus: 'PAID',
    type: '',
    isJunk: '',
  });

  const { page, limit, search, orderStatus, paymentStatus, type, isJunk } =
    params;

  // ✅ Local UI state
  const [state, setState] = useState<IOrderStates>({
    order: [],
    pageInfo: {
      total: 10,
      totalPages: 1,
      limit,
      page,
    },
    showPaymentModel: false,
    isLoading: false,
  });

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
      key: 'selection',
    },
  ]);

  const handleChangeState = (payload: Partial<IOrderStates>) => {
    setState((prev) => ({ ...prev, ...payload }));
  };

  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(state.order);

  const fetchOrders = async () => {
    try {
      handleChangeState({ isLoading: true });

      const startDate = formatDateTime(dateRange[0].startDate, 'YYMMDD');
      const endDate = formatDateTime(dateRange[0].endDate, 'YYMMDD');

      const paramsForApi: Record<string, any> = {
        page,
        limit,
        search,
        status: orderStatus,
        type,
        startDate,
        endDate,
      };

      if (!isJunk) {
        paramsForApi.paymentStatus = paymentStatus;
      }

      if (isJunk) {
        paramsForApi.isJunk = true;
      }

      const query = GetFilterUrl(paramsForApi);
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}?${query}`;

      const res = await callApi<IOrderResponse>(PATH, { method: 'GET' });

      handleChangeState({
        order: res.data,
        pageInfo: res.pageInfo,
        isLoading: false,
      });
    } catch {
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [
    page,
    limit,
    search,
    orderStatus,
    paymentStatus,
    type,
    isJunk,
    dateRange,
  ]);

  // 🔹 Payment status update
  const updatePaymentStatus = async () => {
    if (!selectedOrderId) return;

    try {
      handleChangeState({ isLoading: true });

      await callApi(
        `${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/update-status`,
        {
          method: 'PATCH',
          data: { id: selectedOrderId, paymentStatus: 'PAID' },
        },
      );

      showToastNotification('success', 'Payment status updated to paid.');
      fetchOrders();
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ isLoading: false, showPaymentModel: false });
      setSelectedOrderId(null);
    }
  };

  // 🔹 Filter change handler
  const handleFilterChange = (filter: string) => {
    // Payment based filters
    if (filter === 'PAID' || filter === 'UNPAID') {
      setParams({
        paymentStatus: filter,
        isJunk: '',
        orderStatus: '',
        page: 1,
      });
      return;
    }

    // Junk orders → REMOVE paymentStatus
    if (filter === 'isJunk') {
      setParams({
        isJunk: 'true',
        paymentStatus: '',
        orderStatus: '',
        page: 1,
      });
      return;
    }

    // Normal order status filter
    setParams({
      orderStatus: filter,
      paymentStatus: 'PAID',
      isJunk: '',
      page: 1,
    });
  };

  const activeFilter = useMemo(() => {
    if (isJunk === 'true') return 'isJunk';
    if (paymentStatus && paymentStatus !== 'PAID') return paymentStatus;
    if (orderStatus) return orderStatus;
    return '';
  }, [isJunk, paymentStatus, orderStatus]);

  // const handleFilterChange = (filter: string) => {
  //   const paymentStatuses = ['PAID', 'UNPAID'];

  //   let newState: Partial<IOrderStates> = {
  //     orderStatus: '',
  //     paymentStatus: '',
  //     isJunk: null,
  //     pageInfo: { ...state.pageInfo, page: 1 },
  //   };

  //   if (paymentStatuses.includes(filter)) {
  //     newState.paymentStatus = filter;
  //   } else if (filter === 'isJunk') {
  //     newState.isJunk = true;
  //   } else {
  //     newState.orderStatus = filter;
  //     newState.paymentStatus = 'PAID';
  //   }

  //   handleChangeState(newState);
  // };

  // 🔹 Table columns
  const orderColumns: TableColumn<Order>[] = [
    {
      key: 'orderId',
      header: 'Order ID',
      render: (order) => (
        <span className={order.personalInfo.isEmployee ? 'text-blue-500' : ''}>
          {order.orderId}
        </span>
      ),
    },
    {
      key: 'dateTime',
      header: 'Date/Time',
      render: (order) => {
        const deliveredInfo = order.previousStatus?.find(
          (v) => v.status === OrderStatus.DELIVERED,
        );
        return deliveredInfo?.timestamp
          ? formatDateTime(deliveredInfo.timestamp, 'YYMMMDDHH')
          : formatDeliveryRange(order.formattedDeliveryTime);
      },
    },
    {
      key: 'customer',
      header: 'Customer',
      render: (order) =>
        `${order.personalInfo.firstName} ${order.personalInfo.lastName}`,
    },
    {
      key: 'total',
      header: 'Total Amount BD',
      render: (order) => toFixedThree(order.grandTotal),
    },
    {
      key: 'orderStatus',
      header: 'Order Status',
      render: (order) => (
        <OrderStatusUpdate order={order} onSucess={fetchOrders} />
      ),
    },
    {
      key: 'paymentType',
      header: 'Payment Type',
      render: (order) =>
        getPaymentMethodMessage(
          order.paymentMethod?.type,
          order.paymentStatus,
        ) ?? 'N/A',
    },
    {
      key: 'items',
      header: 'Items',
      render: (order) => order.orderItems?.length ?? 0,
    },
    {
      key: 'paymentStatus',
      header: 'Payment Status',
      render: (item) => (
        <StatusDropdown
          value={item.paymentStatus}
          options={
            item.paymentStatus !== 'PAID'
              ? [{ value: 'PAID', label: 'Paid' }]
              : []
          }
          onChange={() => {
            handleChangeState({ showPaymentModel: true });
            setSelectedOrderId(item.id);
          }}
          canEditVal="order"
        />
      ),
    },
  ];

  const orderActionConfig: Record<string, ActionConfig<Order>> = {
    view: {
      text: 'View',
      onClick: (order) => navigate(`${RoutesLink.orderDetail}/${order.id}`),
      permission: () => canView('order'),
    },
  };

  const actions = useMemo(
    () =>
      Object.values(orderActionConfig)
        .filter((a) => a.permission())
        .map(({ permission, ...rest }) => rest),
    [canView, canEdit],
  );

  const filterOptions = [
    { value: 'customers', label: 'Customers' },
    { value: 'employee', label: 'Employees' },
  ];

  return (
    <Layout>
      <PageHeading text="Orders" />

      <Card>
        <div className=" flex flex-col lg:flex-row w-full mb-4 items-start flex-wrap lg:items-center justify-between gap-3 ">
          <CommonFilter
            activeFilter={activeFilter}
            filters={orderFilterOption}
            inputPlaceHolder="Search for Orders..."
            searchValue={search}
            onSearch={(text) => setParams({ search: text })}
            onChange={handleFilterChange}
            filterOptions={{
              values: filterOptions,
              onChange: (value) => setParams({ type: value }),
            }}
          />

          <div className="flex items-center gap-3">
            <DateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
            {state.order.length > 0 && (
              <ExportOption
                APIPath={`${API_URL_ORDER_SERVICE}${API_ENDPOINT.order}/download-report`}
                dateRange={dateRange[0]}
                status={orderStatus}
              />
            )}
          </div>
        </div>

        <CommonTable
          columns={orderColumns}
          data={state.order}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No Order found"
          loading={state.isLoading}
          actions={actions}
        />

        <Pagination
          paginationData={state.pageInfo}
          onPageChange={(p) => setParams({ page: p })}
          onPageSizeChange={(l) => setParams({ limit: l })}
        />

        {isModalOpen && selectedOrderId && (
          <AssignDriver
            id={selectedOrderId}
            setIsOpen={setIsModalOpen}
            onSuccess={() => {
              fetchOrders();
              setSelectedOrderId(null);
            }}
          />
        )}

        {state.showPaymentModel && (
          <ConfirmationModel
            modelType="update"
            show
            type="Paid"
            subTitle="Are you sure you want to update payment status to"
            onClose={() => {
              handleChangeState({ showPaymentModel: false });
              setSelectedOrderId(null);
            }}
            onConfirm={updatePaymentStatus}
          />
        )}
      </Card>
    </Layout>
  );
};

export default Orders;
